import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineClock,
  HiOutlinePencil,
  HiOutlineTrash,
} from 'react-icons/hi2';
import { authService, machineService, productService } from '../../services';
import masterDataService from '../../services/masterDataService';
import SharedModal from '../../components/common/SharedModal';
import PageLoading from '../../components/common/PageLoading';

const CycleTimes = () => {
  const [cycleTimes, setCycleTimes] = useState([]);
  const [machines, setMachines] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [machineFilter, setMachineFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCycleTime, setEditingCycleTime] = useState(null);
  const [formData, setFormData] = useState({
    machineId: '',
    productId: '',
    productCode: '',
    idealCycleTime: '',
    status: 'Active',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadCycleTimes();
      loadMachines();
      loadProducts();
    } else {
      setError('Please login to view cycle times');
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (tenantId) {
      loadCycleTimes();
    }
  }, [machineFilter, productFilter, tenantId]);

  const loadCycleTimes = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await masterDataService.getCycleTimes(
        tenantId,
        machineFilter || null,
        productFilter || null
      );
      setCycleTimes(data);
    } catch (err) {
      setError('Failed to load cycle times. Please try again.');
      console.error('Error loading cycle times:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMachines = async () => {
    try {
      const data = await machineService.getAllMachines();
      setMachines(data);
    } catch (err) {
      console.error('Error loading machines:', err);
    }
  };

  const loadProducts = async () => {
    try {
      const data = await productService.getAllProducts();
      setProducts(data);
    } catch (err) {
      console.error('Error loading products:', err);
    }
  };

  const getMachineName = (machineId) => {
    if (!machineId) return '-';
    const machine = machines.find(m => m.machineId === machineId);
    return machine ? machine.machineName : '-';
  };

  const getProductName = (productId) => {
    if (!productId) return '-';
    const product = products.find(p => p.productId === productId);
    return product ? product.productName : '-';
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredCycleTimes = cycleTimes.filter((ct) =>
    ct.productCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getMachineName(ct.machineId)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getProductName(ct.productId)?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingCycleTime(null);
    setFormData({
      machineId: '',
      productId: '',
      productCode: '',
      idealCycleTime: '',
      status: 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (cycleTime) => {
    setEditingCycleTime(cycleTime);
    setFormData({
      machineId: cycleTime.machineId ? cycleTime.machineId.toString() : '',
      productId: cycleTime.productId ? cycleTime.productId.toString() : '',
      productCode: cycleTime.productCode || '',
      idealCycleTime: cycleTime.idealCycleTime ? cycleTime.idealCycleTime.toString() : '',
      status: cycleTime.status || 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      await masterDataService.deleteCycleTime(id);
      await loadCycleTimes();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete cycle time. Please try again.');
      console.error('Error deleting cycle time:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    const idealVal = parseFloat(formData.idealCycleTime);

    if (!formData.idealCycleTime) {
      errors.idealCycleTime = "Ideal cycle time is required";
    } else if (isNaN(idealVal) || idealVal <= 0) {
      errors.idealCycleTime = "Ideal cycle time must be greater than 0";
    }

    if (formData.productCode && formData.productCode.length > 50) {
      errors.productCode = "Product code must be less than 50 characters";
    }

    // Duplicate check
    const isDuplicate = cycleTimes.some(
      (ct) =>
        ct.machineId?.toString() === formData.machineId?.toString() &&
        ct.productId?.toString() === formData.productId?.toString() &&
        ct.productCode?.trim().toLowerCase() ===
          (formData.productCode || "").trim().toLowerCase() &&
        ct.cycleId !== editingCycleTime?.cycleId,
    );
    if (isDuplicate) {
      errors.idealCycleTime =
        "Cycle time entry already exists for this Machine and Product";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const cycleTimeData = {
        tenantId: tenantId,
        machineId: formData.machineId ? parseInt(formData.machineId) : null,
        productId: formData.productId ? parseInt(formData.productId) : null,
        productCode: formData.productCode.trim() || null,
        idealCycleTime: parseFloat(formData.idealCycleTime),
        status: formData.status,
        machine: null, // Navigation property
        product: null // Navigation property
      };

      if (editingCycleTime) {
        cycleTimeData.cycleId = editingCycleTime.cycleId;
        await masterDataService.updateCycleTime(editingCycleTime.cycleId, cycleTimeData);
      } else {
        await masterDataService.createCycleTime(cycleTimeData);
      }

      await loadCycleTimes();
      setShowModal(false);
      setFormData({ machineId: '', productId: '', productCode: '', idealCycleTime: '', status: 'Active' });
      setEditingCycleTime(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save cycle time. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCycleTime(null);
    setFormData({ machineId: '', productId: '', productCode: '', idealCycleTime: '', status: 'Active' });
    setFormErrors({});
  };

  if (loading) {
    return <PageLoading message="Loading cycle times..." />;
  }

  return (
    <div className="cycle-times-page">
      <div className="page-header">
        <div className="header-content">
          <h2>Cycle Times</h2>
          <p>Manage ideal cycle times for machines and products</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus className="btn-icon" />
          Add Cycle Time
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search cycle times..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="filter-group">
            <select
              value={machineFilter}
              onChange={(e) => setMachineFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Machines</option>
              {machines.map((m) => (
                <option key={m.machineId} value={m.machineId}>
                  {m.machineName}
                </option>
              ))}
            </select>
            <select
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Products</option>
              {products.map((p) => (
                <option key={p.productId} value={p.productId}>
                  {p.productName}
                </option>
              ))}
            </select>
          </div>
          <div className="table-info">
            {filteredCycleTimes.length} {filteredCycleTimes.length === 1 ? 'cycle time' : 'cycle times'}
          </div>
        </div>

        {filteredCycleTimes.length === 0 ? (
          <div className="empty-state">
            <HiOutlineClock className="empty-icon" />
            <h3>No cycle times found</h3>
            <p>
              {searchTerm || machineFilter || productFilter
                ? 'Try adjusting your search or filter terms'
                : 'Get started by creating your first cycle time'}
            </p>
            {!searchTerm && !machineFilter && !productFilter && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Cycle Time
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Machine</th>
                <th>Product</th>
                <th>Product Code</th>
                <th>Ideal Cycle Time (sec)</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCycleTimes.map((ct) => (
                <tr key={ct.cycleId}>
                  <td>{ct.cycleId}</td>
                  <td>{getMachineName(ct.machineId)}</td>
                  <td>{getProductName(ct.productId)}</td>
                  <td>{ct.productCode || '-'}</td>
                  <td className="name-cell">{ct.idealCycleTime ? ct.idealCycleTime.toFixed(3) : '-'}</td>
                  <td>
                    <span className={`status-badge ${ct.status?.toLowerCase()}`}>
                      {ct.status || 'Active'}
                    </span>
                  </td>
                  <td>
                    {ct.createdAt
                      ? new Date(ct.createdAt).toLocaleDateString()
                      : '-'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(ct)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(ct)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingCycleTime ? 'Edit Cycle Time' : 'New Cycle Time'}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={handleCloseModal} disabled={submitting}>Cancel</button>
            <button type="submit" form="cycle-time-form" className="btn-primary" disabled={submitting}>
              {submitting ? (editingCycleTime ? 'Updating...' : 'Creating...') : (editingCycleTime ? 'Update' : 'Create')}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="cycle-time-form" onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="machineId">Machine</label>
                <select id="machineId" value={formData.machineId} onChange={(e) => setFormData({ ...formData, machineId: e.target.value })}>
                  <option value="">Select Machine (Optional)</option>
                  {machines.map((m) => (<option key={m.machineId} value={m.machineId}>{m.machineName} ({m.machineCode})</option>))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="productId">Product</label>
                <select id="productId" value={formData.productId} onChange={(e) => setFormData({ ...formData, productId: e.target.value })}>
                  <option value="">Select Product (Optional)</option>
                  {products.map((p) => (<option key={p.productId} value={p.productId}>{p.productName} {p.productCode ? `(${p.productCode})` : ''}</option>))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="productCode">Product Code</label>
                <input type="text" id="productCode" value={formData.productCode} onChange={(e) => setFormData({ ...formData, productCode: e.target.value })} className={formErrors.productCode ? 'error' : ''} placeholder="Enter product code" maxLength={50} />
                {formErrors.productCode && <span className="field-error">{formErrors.productCode}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="idealCycleTime">Ideal Cycle Time (seconds) <span className="required">*</span></label>
                <input type="number" id="idealCycleTime" value={formData.idealCycleTime} onChange={(e) => setFormData({ ...formData, idealCycleTime: e.target.value })} className={formErrors.idealCycleTime ? 'error' : ''} placeholder="e.g., 2.5, 10.0" step="0.001" min="0.001" />
                {formErrors.idealCycleTime && <span className="field-error">{formErrors.idealCycleTime}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select id="status" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customWidth="400px"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            <button type="button" className="btn-danger" onClick={() => handleDelete(deleteConfirm.cycleId)}>Delete</button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>Are you sure you want to delete this cycle time?</p>
          <p className="warning-text">This action cannot be undone. The cycle time will be marked as inactive.</p>
        </div>
      </SharedModal>
    </div>
  );
};

export default CycleTimes;

